import React, { useState } from 'react';
import { Button } from '../button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../dialog';
import { Delete } from 'lucide-react';

interface NumericKeypadProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: number) => void;
  title: string;
  initialValue?: number;
}

export const NumericKeypad: React.FC<NumericKeypadProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialValue = 0
}) => {
  const [displayValue, setDisplayValue] = useState<string>(initialValue.toString());
  const [isFirstInput, setIsFirstInput] = useState<boolean>(true);

  const handleNumberClick = (number: string) => {
    if (isFirstInput) {
      // Al primer input, reemplazar el valor inicial
      setDisplayValue(number);
      setIsFirstInput(false);
    } else if (displayValue === '0') {
      setDisplayValue(number);
    } else {
      setDisplayValue(displayValue + number);
    }
  };

  const handleBackspace = () => {
    if (displayValue.length > 1) {
      setDisplayValue(displayValue.slice(0, -1));
    } else {
      setDisplayValue('0');
    }
    setIsFirstInput(false);
  };

  const handleClear = () => {
    setDisplayValue('0');
    setIsFirstInput(true);
  };

  const handleSubmit = () => {
    const numValue = parseInt(displayValue) || 0;
    onSubmit(numValue);
    onClose();
  };

  const handleClose = () => {
    setDisplayValue(initialValue.toString());
    setIsFirstInput(true);
    onClose();
  };

  // Reset display value when modal opens with new initial value
  React.useEffect(() => {
    if (isOpen) {
      setDisplayValue(initialValue.toString());
      setIsFirstInput(true);
    }
  }, [isOpen, initialValue]);

  const keypadButtons = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['C', '0', '⌫']
  ];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg text-center">{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Display */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
            <div className="text-right text-2xl font-bold text-gray-800 min-h-[2.5rem] flex items-center justify-end">
              {displayValue}
            </div>
          </div>

          {/* Keypad */}
          <div className="grid grid-cols-3 gap-3">
            {keypadButtons.flat().map((button, index) => {
              const isNumber = !isNaN(parseInt(button));
              const isClear = button === 'C';
              const isBackspace = button === '⌫';
              const isZero = button === '0';

              return (
                <Button
                  key={index}
                  variant="outline"
                  className={`
                    h-12 text-lg font-semibold transition-all duration-150 active:scale-95
                    ${isNumber || isZero ? 'bg-white hover:bg-gray-50 border-gray-300' : ''}
                    ${isClear ? 'bg-red-50 hover:bg-red-100 border-red-200 text-red-700' : ''}
                    ${isBackspace ? 'bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700' : ''}
                  `}
                  onClick={() => {
                    if (isNumber || isZero) {
                      handleNumberClick(button);
                    } else if (isClear) {
                      handleClear();
                    } else if (isBackspace) {
                      handleBackspace();
                    }
                  }}
                >
                  {isBackspace ? <Delete className="h-5 w-5" /> : button}
                </Button>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="h-12 text-lg"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              className="h-12 text-lg bg-macartur-primary hover:bg-macartur-primary/90"
            >
              Confirmar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};